import type { Root } from 'mdast';
import type { Plugin } from 'unified';

import { visit } from 'unist-util-visit';

/**
 * Remark plugin to detect GitHub-style alert syntax in blockquotes
 * and add data attributes for styling.
 *
 * Transforms blockquotes like:
 * > [!SUMMARY]
 * > Content here
 *
 * Into blockquotes with data-alert-type attribute.
 */
export const remarkAlert: Plugin<[], Root> = () => {
  return (tree) => {
    visit(tree, 'blockquote', (node) => {
      // Extract text from the blockquote children
      const extractText = (nodes: typeof node.children): string => {
        return nodes
          .map((child) => {
            if (child.type === 'paragraph') {
              return child.children
                .map((c) => {
                  if (c.type === 'text') {
                    return c.value;
                  }

                  return '';
                })
                .join('');
            }
            if (child.type === 'text') {
              return child.value;
            }
            return '';
          })
          .join(' ')
          .trim();
      };

      const textContent = extractText(node.children);

      // Check for alert pattern    [!ALERTTYPE]
      const alertMatch = textContent.match(/\[!\s*(\w+)\s*\]/);

      if (alertMatch?.[1]) {
        const alertType = alertMatch[1].toLowerCase();

        // Add data attribute to the node
        node.data = {
          ...node.data,
          hProperties: {
            ...(node.data?.hProperties as Record<string, unknown>),
            'data-alert-type': alertType,
          },
        };

        // Remove the first occurrence of the alert marker from the blockquote
        // Iterate all paragraphs and their text nodes to find and remove it
        for (let i = 0; i < node.children.length; i++) {
          const child = node.children[i];

          if (child.type !== 'paragraph') {
            continue;
          }

          let markerRemoved = false;

          for (let j = 0; j < child.children.length; j++) {
            const textNode = child.children[j];

            if (textNode.type !== 'text') {
              continue;
            }

            // Only remove the first match (no global flag)
            const cleaned = textNode.value.replace(/\[!\s*\w+\s*\]\s*/, '');

            if (cleaned !== textNode.value) {
              // Marker was found and removed
              const trimmed = cleaned.trim();

              if (trimmed) {
                textNode.value = trimmed;
              } else {
                // Remove the text node if it's empty after cleaning
                child.children.splice(j, 1);

                // If paragraph is now empty, remove it
                if (child.children.length === 0) {
                  node.children.splice(i, 1);
                }
              }

              markerRemoved = true;
              break;
            }
          }

          if (markerRemoved) {
            break;
          }
        }
      }
    });
  };
};
